import { createInfiniteScroll } from "@utils/createInfiniteScroll";
import { createVisiblePoll } from "@utils/createVisiblePoll";
import type { Accessor } from "solid-js";
import type { BoardItemListItemResponse } from "@/client";
import { listBoardClips } from "@/client";
import {
	hasRenderingClips,
	RENDERING_POLL_INTERVAL_MS,
	refreshLoadedBoardClips,
} from "./boardClipsList";

type ListClips = (args: {
	path: { board_id: string };
	query?: {
		page?: number;
		sort?: string;
		asc?: boolean;
		search?: string;
	};
}) => Promise<{ data?: BoardItemListItemResponse[] }>;

export function useBoardClipsList(
	boardId: () => string,
	search: Accessor<string>,
	listClips: ListClips = listBoardClips,
) {
	const fetchBoardClipsPage = async (page: number) => {
		const trimmedSearch = search().trim();
		const res = await listClips({
			path: { board_id: boardId() },
			query: {
				page,
				sort: "created_at",
				asc: false,
				...(trimmedSearch ? { search: trimmedSearch } : {}),
			},
		});
		return res.data ?? [];
	};

	const [
		clips,
		setEl,
		{ end, reset, page, setPages },
		{ mutate, loading, error, restoreLastMutation },
	] = createInfiniteScroll<BoardItemListItemResponse>(fetchBoardClipsPage);

	// `loading` only covers page fetches, so the refresh in flight is tracked
	// here to stop ticks stacking up one request per loaded page.
	let refreshing = false;

	const refreshLoadedPages = async () => {
		refreshing = true;
		const snapshot = clips();
		try {
			const merged = await refreshLoadedBoardClips(
				page(),
				snapshot,
				fetchBoardClipsPage,
			);
			// A page appended, a reset or an edit while the requests were in
			// flight makes the snapshot stale, and writing it back would drop
			// that change. The next tick refreshes again.
			if (clips() === snapshot) {
				setPages(merged);
			}
		} catch {
			// A failed refresh is retried by the next tick.
		} finally {
			refreshing = false;
		}
	};

	createVisiblePoll({
		enabled: () => hasRenderingClips(clips()),
		refetch: refreshLoadedPages,
		loading: () => loading() || refreshing,
		intervalMs: RENDERING_POLL_INTERVAL_MS,
	});

	return {
		clips,
		setEl,
		end,
		reset,
		mutate,
		loading,
		error,
		restoreLastMutation,
	};
}
